import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  ArrowLeft,
  Brain,
  ClipboardList,
  FileText,
  Sparkles,
  Loader2,
  AlertCircle,
  Crown,
  CheckCircle,
  HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { analytics } from '../utils/analytics';
import { API_URL } from '../config/api';

export default function Study() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { accessToken, loading: authLoading, canGenerate, remainingGenerations, dailyLimit, isSubscribed, refreshProfile } = useAuth();
  const [session, setSession] = useState(null);
  const [contents, setContents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [generatingType, setGeneratingType] = useState(null);
  const [generationProgress, setGenerationProgress] = useState('');
  const fetchedRef = useRef(false);
  const pollingRef = useRef(null);

  const fetchSession = useCallback(async () => {
    if (!accessToken || !sessionId) return;

    try {
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSession(data);
        setLoadError(null);
      } else if (response.status === 404) {
        setLoadError('Session not found');
      } else if (response.status === 403) {
        setLoadError('Session access denied');
      } else {
        setLoadError('Failed to load session');
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
      setLoadError('Network error. Please refresh the page.');
    }
  }, [accessToken, sessionId]);

  const fetchContents = useCallback(async () => {
    if (!accessToken || !sessionId) return;

    try {
      const response = await fetch(`${API_URL}/api/content/session/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContents(data.contents || []);
      }
    } catch (error) {
      console.error('Failed to fetch contents:', error);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, sessionId]);

  useEffect(() => {
    // Wait for auth to be ready before fetching
    if (authLoading) return;

    // Prevent double-fetch
    if (fetchedRef.current) return;

    if (!accessToken) {
      navigate('/login');
      return;
    }

    if (sessionId) {
      fetchedRef.current = true;
      fetchSession();
      fetchContents();
    }
  }, [authLoading, accessToken, sessionId, fetchSession, fetchContents, navigate]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const pollGenerationStatus = async (jobId, studyType) => {
    let pollCount = 0;
    const maxPolls = 120; // 2 minutes max (1 poll per second)

    const progressMessages = [
      'Analyzing your PDF content...',
      'Understanding calculus concepts...',
      'Generating personalized content...',
      'Creating detailed explanations...',
      'Almost there...'
    ];

    pollingRef.current = setInterval(async () => {
      pollCount++;

      // Update progress message
      const msgIndex = Math.min(Math.floor(pollCount / 10), progressMessages.length - 1);
      setGenerationProgress(progressMessages[msgIndex]);

      if (pollCount >= maxPolls) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setGeneratingType(null);
        setGenerationProgress('');
        toast.error('Generation timed out. Please try again.');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/generation-status/${jobId}`, {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });

        if (!response.ok) {
          if (response.status === 403) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setGeneratingType(null);
            setGenerationProgress('');
            toast.error('Session expired. Please log in again.');
            return;
          }
          return; // Keep polling on other errors
        }

        const data = await response.json();

        if (data.status === 'completed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setGeneratingType(null);
          setGenerationProgress('');

          // Track generation by type
          if (studyType === 'quiz') analytics.quizGenerated(sessionId);
          else if (studyType === 'exam') analytics.examGenerated(sessionId);
          else if (studyType === 'flashcards') analytics.flashcardsGenerated(sessionId);
          else if (studyType === 'explanation') analytics.explanationGenerated(sessionId);

          toast.success(`${studyType.charAt(0).toUpperCase() + studyType.slice(1)} generated!`);
          await refreshProfile();
          navigate(`/results/${data.content_id}`);
        } else if (data.status === 'failed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setGeneratingType(null);
          setGenerationProgress('');
          toast.error(data.error || 'Generation failed. Please try again.');
        }
        // Keep polling if status is 'pending' or 'processing'
      } catch (error) {
        console.error('Polling error:', error);
        // Don't stop polling on network errors, just log
      }
    }, 1000);
  };

  const handleGenerate = async (studyType) => {
    if (!canGenerate) {
      toast.error("You've reached today's free limit. Upgrade to Pro for unlimited generations or come back tomorrow.");
      analytics.upgradeButtonClicked('generation_limit_reached');
      navigate('/subscription');
      return;
    }

    setGeneratingType(studyType);
    setGenerationProgress('Starting generation...');

    try {
      const response = await fetch(`${API_URL}/api/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId,
          study_type: studyType
        })
      });

      if (response.status === 403) {
        const data = await response.json();
        if (data.detail?.includes('limit')) {
          toast.error("You've reached today's free limit. Upgrade to Pro for unlimited!");
          navigate('/subscription');
        } else {
          toast.error('Session expired. Please try logging in again.');
        }
        setGeneratingType(null);
        setGenerationProgress('');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Generation failed');
      }

      // Start polling for completion
      pollGenerationStatus(data.job_id, studyType);

    } catch (error) {
      const errorMessage = error.message || 'Failed to start generation. Please try again.';
      toast.error(errorMessage);
      setGeneratingType(null);
      setGenerationProgress('');
    }
  };
  const studyCategories = [
    {
      id: 'practice',
      title: 'PRACTICE',
      description: 'Test your knowledge',
      tools: [
        { id: 'quiz', icon: ClipboardList, title: 'Quiz', description: '12-15 questions', color: 'bg-green-500' },
        { id: 'exam', icon: FileText, title: 'Exam', description: '20-question exam', color: 'bg-purple-500' },
        //{ id: 'practice_questions', icon: HelpCircle, title: 'Questions', description: 'Mixed practice', color: 'bg-teal-500' },
        { id: 'true_false', icon: HelpCircle, title: 'True/False', description: 'Quick T/F quiz', color: 'bg-emerald-500' }
      ]
    },
    {
      id: 'learn',
      title: 'LEARN',
      description: 'Understand the material',
      tools: [
        { id: 'explanation', icon: Brain, title: 'Explain', description: 'Clear explanations', color: 'bg-blue-500' },
        //{ id: 'summary', icon: List, title: 'Summary', description: 'Key points', color: 'bg-indigo-500' },
        //{ id: 'key_concepts', icon: Lightbulb, title: 'Key Ideas', description: 'Main concepts', color: 'bg-yellow-500' },
        { id: 'flashcards', icon: Sparkles, title: 'Flashcards', description: 'Quick review', color: 'bg-orange-500' },
        //{ id: 'simplified', icon: Zap, title: 'Simplified', description: 'Easy terms', color: 'bg-pink-500' },
        //{ id: 'study_guide', icon: BookOpen, title: 'Study Guide', description: 'Complete outline', color: 'bg-cyan-500' }
      ]
    },
    {
      id: 'interactive',
      title: 'INTERACTIVE',
      description: 'Engage with games',
      tools: [
        //{ id: 'memory_match', icon: Gamepad2, title: 'Memory', description: 'Match pairs', color: 'bg-rose-500' },
        //{ id: 'speed_review', icon: Zap, title: 'Speed', description: 'Timed review', color: 'bg-amber-500' },
        //{ id: 'video_explanation', icon: Play, title: 'Video', description: 'Audio narration', color: 'bg-violet-500', premium: true }
      ]
    }
  ];
  const studyTools = [
    {
      id: 'explanation',
      icon: Brain,
      title: 'EXPLAIN CONCEPTS',
      description: 'Get step-by-step explanations of key calculus concepts',
      color: 'bg-blue-500'
    },
    {
      id: 'quiz',
      icon: ClipboardList,
      title: 'PRACTICE QUIZ',
      description: '10-15 questions with answers and explanations',
      color: 'bg-green-500'
    },
    {
      id: 'exam',
      icon: FileText,
      title: 'PRACTICE EXAM',
      description: 'Full 20-question exam with solutions',
      color: 'bg-purple-500'
    },
    {
      id: 'flashcards',
      icon: Sparkles,
      title: 'FLASHCARDS',
      description: 'Key definitions and formulas for quick review',
      color: 'bg-orange-500'
    },
    {
      id: 'true_false',
      icon: HelpCircle,
      title: 'TRUE/FALSE',
      description: 'Quick T/F quiz',
      color: 'bg-emerald-500'
    }
  ];

  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading study session...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md p-6">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="font-heading font-bold text-xl mb-2">Error Loading Session</h2>
          <p className="text-muted-foreground mb-6">{loadError}</p>
          <Link to="/dashboard">
            <Button>Go to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b-2 border-border sticky top-0 bg-white z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-black text-lg tracking-tight">CALC AI</span>
          </Link>
          <Link to="/dashboard">
            <Button variant="outline" className="h-10 border-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Session Info */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tight">
            {session?.file_name || 'Study Session'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {session?.file_text?.length || 0} characters extracted
          </p>
        </motion.div>

        {/* Limit Warning / Daily Counter */}
        {!isSubscribed && (
          <motion.div
            className={`mb-8 border-2 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${canGenerate ? 'border-border' : 'border-destructive bg-destructive/5'
              }`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3">
              {canGenerate ? (
                <Sparkles className="w-6 h-6 text-primary" />
              ) : (
                <AlertCircle className="w-6 h-6 text-destructive" />
              )}
              <div>
                <p className="font-medium" data-testid="study-generation-counter">
                  {canGenerate
                    ? `Free generations remaining today: ${remainingGenerations} / ${dailyLimit}`
                    : "You've reached today's free limit"
                  }
                </p>
                <p className="text-sm text-muted-foreground">
                  {canGenerate
                    ? 'Resets daily • Upgrade for unlimited'
                    : 'Upgrade to Pro for unlimited generations or come back tomorrow'
                  }
                </p>
              </div>
            </div>
            <Link to="/subscription">
              <Button className="h-10 px-6 font-bold uppercase text-sm">
                <Crown className="w-4 h-4 mr-2" />
                {canGenerate ? 'Upgrade' : 'Upgrade Now'}
              </Button>
            </Link>
          </motion.div>
        )}

        {/* Generation Progress Indicator */}
        {generatingType && (
          <motion.div
            className="mb-8 border-2 border-primary bg-primary/5 p-6"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
              <div>
                <p className="font-heading font-bold text-lg">
                  Generating {generatingType}...
                </p>
                <p className="text-sm text-muted-foreground">
                  {generationProgress || 'This may take up to 60 seconds'}
                </p>
              </div>
            </div>
          </motion.div>
        )}
        {/* Study Tools - Organized by Category */}
        <div className="mb-12 space-y-8">
          <h2 className="font-heading font-bold text-xl">CHOOSE HOW YOU WANT TO STUDY</h2>

          {studyCategories.filter(c => c.tools.length > 0).map((category) => (
            <div key={category.id}>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="font-heading font-bold text-sm text-muted-foreground tracking-wider">
                  {category.title}
                </h3>
                <span className="text-xs text-muted-foreground">
                  — {category.description}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {category.tools.map((tool, index) => (
                  <motion.button
                    key={tool.id}
                    className={`border-2 border-border p-4 text-left transition-all hover:border-primary rounded-lg relative ${generatingType === tool.id ? 'opacity-50' : ''
                      } ${!canGenerate && !isSubscribed ? 'opacity-50' : ''}`}
                    onClick={() => handleGenerate(tool.id)}
                    disabled={generatingType !== null || (!canGenerate && !isSubscribed)}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`generate-${tool.id}-btn`}
                  >
                    {tool.premium && !isSubscribed && (
                      <div className="absolute top-2 right-2">
                        <Crown className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <div className={`w-10 h-10 ${tool.color} flex items-center justify-center rounded-lg mb-2`}>
                      {generatingType === tool.id ? (
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                      ) : (
                        <tool.icon className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <h4 className="font-bold text-sm">{tool.title}</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {generatingType === tool.id ? 'Generating...' : tool.description}
                    </p>
                  </motion.button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Previously Generated Content */}
        {contents.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="font-heading font-bold text-lg mb-4">PREVIOUSLY GENERATED</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {contents.map((content, index) => {
                const tool = studyTools.find(t => t.id === content.study_type);
                return (
                  <motion.div
                    key={content.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={`/results/${content.id}`}
                      className="block border-2 border-border p-4 hover:border-primary transition-colors"
                      data-testid={`content-card-${content.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${tool?.color || 'bg-primary'} flex items-center justify-center`}>
                          {tool && <tool.icon className="w-5 h-5 text-white" />}
                        </div>
                        <div>
                          <p className="font-medium capitalize">{content.study_type}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(content.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
        {/* Footer */}
        <footer className="border-t-2 border-border py-8 px-6 bg-white">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold text-sm">CALC AI</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © Powered by FHYAS LLC.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}

